import { Injectable } from '@angular/core';
import { Backend } from '@DataFriends-Group/ngx-core';
import { map, Observable } from 'rxjs';

/**
 * Enum representing different box sizes.
 *
 * - `S`: Small size (value: 1).
 * - `M`: Medium size (value: 2).
 * - `L`: Large size (value: 3).
 */
export enum BoxSize {
  S = 1,
  M = 2,
  L = 3,
}

/**
 * Represents a request to open a type of box with specific properties.
 *
 * This class contains the necessary parameters to define the characteristics
 * of the box being requested to open, such as whether it is cooled and its size.
 *
 * @class
 * @param {boolean} isCooled - Indicates if the box is temperature-controlled (cooled).
 * @param {BoxSize} size - Specifies the size of the box.
 */
export class OpenTypeBoxRequest {
  constructor(
    public isCooled: boolean,
    public size: BoxSize,
  ) {}
}

/**
 * Represents information about a free box, including its count, cooling status, size, and type.
 *
 * @interface FreeBoxInfo
 * @property {number} freeCount - The total number of free boxes available.
 * @property {boolean} isCooled - Indicates whether the free box is equipped with cooling functionality.
 * @property {string} size - The size category or dimensions of the free box.
 * @property {string} type - The type or classification of the free box.
 */
export interface FreeBoxInfo {
  freeCount: number;
  isCooled: boolean;
  size: string;
  type: string;
}

/**
 * Represents the response object for an opened box.
 *
 * This interface is used to encapsulate the details of a box once it is opened.
 *
 * @interface OpenBoxResponse
 *
 * @property {number} boxId - The unique identifier of the opened box.
 */
export interface OpenBoxResponse {
  boxId: number;
}

/**
 * Service for interacting with the backend API for managing Box-related operations.
 * Extends the Backend class to leverage common API interaction functionality.
 * It is provided at the root level and can be injected into components or services.
 */
@Injectable({
  providedIn: 'root',
})
export class BoxService extends Backend<unknown, unknown> {
  constructor() {
    super('box');
  }

  /**
   * Fetches a list of free boxes from the specified API endpoint.
   *
   * @return {Observable<FreeBoxInfo[]>} An observable that emits an array of FreeBoxInfo objects if the request is successful, or an empty array if not.
   */
  freeBox(): Observable<FreeBoxInfo[]> {
    return this.get(this.apiUrl + 'free').pipe(
      map((result) => {
        if (result.success) {
          return result.response;
        } else return [];
      }),
    );
  }

  /**
   * Sends a request to open a type box and returns the response.
   *
   * @param {OpenTypeBoxRequest} request - The request object containing the data needed to open the type box.
   * @return {Observable<OpenBoxResponse>} An Observable that emits the response containing the open type box data.
   */
  openTypeBox(request: OpenTypeBoxRequest): Observable<OpenBoxResponse> {
    return this.post(this.apiUrl + 'open-type', request).pipe(map((result) => result.response));
  }
}
